package dev.lingvo.site.pages.app.projects.slug.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.BillingOverviewDto
import dev.lingvo.shared.Plan
import dev.lingvo.shared.PlanStatus
import dev.lingvo.shared.Role
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.components.statusWords
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LChartIcon
import dev.lingvo.site.ds.LCoinsIcon
import dev.lingvo.site.ds.LCreditCardIcon
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSegmentBar
import dev.lingvo.site.ds.LStatDelta
import dev.lingvo.site.ds.LStatTile
import dev.lingvo.site.ds.LStatRow
import dev.lingvo.site.ds.LStatRowItem
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.enterStaggered
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * What this project can spend, and where the plan itself is changed.
 *
 * **Four numbers and one link, not a second billing page.** This tab used to draw the whole
 * workspace billing surface — the plan comparison grid with its checkout buttons, the cancel-
 * subscription control, a thirty-day spend chart and two credit-allocation tables — inside a page
 * scoped to one project. Every one of those is workspace-wide, and every one of them already exists
 * on `/app/settings/billing`, so a workspace with nine projects offered nine places to start a
 * checkout and nine copies of the same numbers to disagree with each other.
 *
 * What belongs here is the part that answers a project question: can a pre-translate run on this
 * project spend anything today. That is the credit balance and the plan's status; the decision to
 * change either is a workspace decision and this hands it over rather than repeating it.
 *
 * Credits are the workspace's, not the project's, and the caption says so — a reader who takes a
 * shared balance for a per-project allowance plans the wrong month.
 */
@Composable
internal fun WorkspacePlanUsage(
    orgSlug: String,
    role: Role?,
    onManagePlan: () -> Unit,
    loadBilling: suspend (String) -> BillingOverviewDto = ApiClient::workspaceBilling,
) {
    var refreshKey by remember(orgSlug) { mutableStateOf(0) }
    // A token, not a key: a retry after a failed refresh must not blank the numbers somebody is
    // reading, and re-keying `loadAsync` on a counter discards them before the request is sent.
    val reloadable = loadReloadable(orgSlug, reloadToken = refreshKey) { loadBilling(orgSlug) }
    SettingsSection(
        title = t("app.projects.slug.settings.workspace_plan_usage.plan_and_usage", "Plan and usage"),
        description = t("app.projects.slug.settings.workspace_plan_usage.the_current_plan_and_machine_translation", "The current plan and machine-translation usage for this project's workspace."),
        wide = true,
        icon = { LChartIcon() },
        actions = {
            // The one thing this tab can do. Not "Compare plans" and not "Cancel" — both are
            // decisions about the workspace, and both live one press away on the page this opens.
            LButton(
                label = t(
                    "app.projects.slug.settings.workspace_plan_usage.manage_the_workspace_plan",
                    "Manage the workspace plan",
                ),
                onClick = onManagePlan,
                variant = LButtonVariant.SECONDARY,
                leadingIcon = { LCreditCardIcon() },
            )
        },
    ) {
        // A failed refresh over numbers that already loaded stays beside them. Credits are the
        // figure somebody checks before spending, and silently showing a stale balance under a
        // heading that says "this month" is the one way this panel can mislead.
        reloadable.refreshError?.let { failure ->
            LAlert(
                t(
                    "app.projects.slug.settings.workspace_plan_usage.showing_the_last_balance_that_loaded",
                    "Showing the last balance that loaded",
                ),
                failure.readable(),
                modifier = Modifier.fillMaxWidth(),
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }
        LAsyncContent(
            reloadable.state,
            rows = 3,
            loadingLabel = t("app.projects.slug.settings.workspace_plan_usage.loading_workspace_plan", "Loading workspace plan"),
            onRetry = { refreshKey++ },
        ) { billing ->
            ProjectPlanSummary(billing, role)
        }
    }
}

/**
 * The plan, the balance, and the two counts that explain it.
 *
 * `LStatTile` rather than four cards: this is a row of counters, and the design system already knows
 * how a row of counters is spaced, aligned and read out. Remaining carries the emphasis because it
 * is the only figure here that can stop a machine-translation run, and its tone turns on the state
 * that stops it rather than on a threshold somebody guessed.
 */
@Composable
private fun ProjectPlanSummary(billing: BillingOverviewDto, role: Role?) {
    val presentation = workspacePlanPresentation(billing, role)
    Column(Modifier.fillMaxWidth().gap(Space.MD).attr("data-project-plan-summary", "")) {
        BillingStatusAlert(billing)
        presentation.zeroCreditNote?.let {
            LAlert(
                t("app.projects.slug.settings.workspace_plan_usage.no_lingvo_credits_left", "No Lingvo credits left"),
                it,
                variant = LAlertVariant.WARNING,
            )
        }
        Div(cardGridModifier("plan-summary").attr("data-plan-summary-grid", "").toAttrs()) {
            LStatTile(
                label = t("app.projects.slug.settings.workspace_plan_usage.tile_plan", "Plan"),
                value = presentation.currentPlanName,
                icon = { LCreditCardIcon() },
                delta = LStatDelta(billing.status.statusWords()),
            )
            LStatTile(
                label = t("app.projects.slug.settings.workspace_plan_usage.tile_included", "Included this month"),
                value = formatCount(billing.creditsIncluded),
                icon = { LCoinsIcon() },
            )
            LStatTile(
                label = t("app.projects.slug.settings.workspace_plan_usage.tile_used", "Used this month"),
                value = formatCount(billing.creditsUsed),
                icon = { LChartIcon() },
            )
            LStatTile(
                label = t("app.projects.slug.settings.workspace_plan_usage.tile_remaining", "Credits left"),
                value = formatCount(billing.creditsRemaining),
                icon = { LCoinsIcon() },
                emphasis = true,
                tone = if (billing.creditsRemaining == 0) LBadgeVariant.WARNING else LBadgeVariant.SUCCESS,
            )
        }
        LMuted(
            t(
                "app.projects.slug.settings.workspace_plan_usage.credits_are_shared_by_every_project",
                "Credits are shared by every project in this workspace. Translating by hand, " +
                    "import, export, review and QA never spend them.",
            ),
        )
    }
}

@Composable
internal fun WorkspacePlanUsageSurface(
    state: Async<BillingOverviewDto>,
    role: Role?,
    orgSlug: String,
    onBillingRetry: () -> Unit,
    notify: (String, LToastVariant, Long) -> Int,
    usageTrendContent: (@Composable () -> Unit)? = null,
    spendContent: (@Composable () -> Unit)? = null,
    onCancelSubscription: (() -> Unit)? = null,
) {
    LAsyncContent(
        state,
        rows = 4,
        loadingLabel = t("app.projects.slug.settings.workspace_plan_usage.loading_workspace_plan", "Loading workspace plan"),
        onRetry = onBillingRetry,
    ) { billing ->
        // `WorkspacePlanUsageContent` emits the alerts, the plan card and the plan comparison as
        // siblings, and `SettingsSection`'s column is what spaced them apart. The content slot is
        // one plain `<div>`, so that column has to be reinstated inside it or the cards touch.
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            WorkspacePlanUsageContent(
                billing = billing,
                role = role,
                orgSlug = orgSlug,
                onChanged = onBillingRetry,
                notify = notify,
                onCancelSubscription = onCancelSubscription,
            )
        }
    }
    usageTrendContent?.invoke() ?: WorkspaceUsageTrend(orgSlug)
    spendContent?.invoke() ?: WorkspaceSpendReports(orgSlug)
}

@Composable
internal fun WorkspacePlanUsageContent(
    billing: BillingOverviewDto,
    role: Role?,
    orgSlug: String,
    onChanged: () -> Unit,
    notify: (String, LToastVariant, Long) -> Int,
    /**
     * Opens the workspace's cancel page, or null where there is nowhere to go.
     *
     * A callback rather than `rememberPageContext()` inside this composable, because
     * `ProjectSettingsGeometryTest` renders it directly to measure the settings tabs and
     * `PageContext` is only valid inside a `@Page`. `Keys.kt` threads navigation the same way, for
     * the same reason.
     */
    onCancelSubscription: (() -> Unit)? = null,
) {
    val presentation = workspacePlanPresentation(billing, role)
    val scope = rememberCoroutineScope()
    var pendingPlan by remember { mutableStateOf<Plan?>(null) }
    var actionError by remember { mutableStateOf<String?>(null) }

    // "Billing is read-only" describes a state and answers a different question — an admin can read
    // billing perfectly well. The title carries the answer, as it does on `billing/Plans.kt`.
    presentation.permissionNote?.let { LAlert(t("app.projects.slug.settings.workspace_plan_usage.billing_is_read_only", "Only an owner can change the plan"), it, variant = LAlertVariant.INFO) }
    if (billing.pendingCheckoutStartedAt != null) {
        LAlert(
            t("app.projects.slug.settings.workspace_plan_usage.confirming_your_checkout", "Confirming your checkout"),
            t("app.projects.slug.settings.workspace_plan_usage.payment_is_still_being_confirmed_do", "Payment is still being confirmed. Do not start another checkout."),
            variant = LAlertVariant.INFO,
        )
    }
    if (billing.degraded) {
        LAlert(
            t("app.projects.slug.settings.workspace_plan_usage.billing_data_may_be_stale", "Billing data may be stale"),
            t("app.projects.slug.settings.workspace_plan_usage.showing_the_latest_saved_billing_data", "Showing the latest saved billing data. Plan changes are unavailable until billing reconnects."),
            variant = LAlertVariant.WARNING,
        )
    }
    BillingStatusAlert(billing)
    actionError?.let {
        LAlert(t("app.projects.slug.settings.workspace_plan_usage.couldnt_change_the_plan", "Couldn't change the plan"), it, variant = LAlertVariant.DANGER, live = true)
    }
    CurrentPlanSummary(billing, presentation) {
        if (role == Role.OWNER && billing.hasSubscription && billing.status != PlanStatus.CANCELING) {
            // **To the page that answers the question, not a second dialog that answers less.**
            // Both entry points called the same endpoint, but this one disclosed only "nothing is
            // deleted" — never the monthly credits dropping to the Free allowance on the end date,
            // nor how many owner/admin seats exceed Free's limit of one, which `billing/cancel`
            // computes from this workspace's own numbers and calls the decision-changing figure.
            LButton(
                label = t("app.projects.slug.settings.workspace_plan_usage.cancel_subscription", "Cancel subscription"),
                onClick = { onCancelSubscription?.invoke() },
                variant = LButtonVariant.GHOST,
                enabled = !presentation.actionsBlocked,
            )
        }
    }
    CreditAllocation(billing, presentation.zeroCreditNote)
    PlanComparison(
        billing = billing,
        role = role,
        pending = pendingPlan,
        onChoose = { plan ->
            pendingPlan = plan
            actionError = null
            scope.launch {
                try {
                    window.location.assign(ApiClient.startWorkspaceCheckout(orgSlug, plan).url)
                } catch (cancelled: CancellationException) {
                    throw cancelled
                } catch (failure: Throwable) {
                    pendingPlan = null
                    actionError = failure.message ?: tr(
                        "app.projects.slug.settings.workspace_plan_usage.check_your_connection",
                        "Check your connection and retry.",
                    )
                }
            }
        },
    )
}

@Composable
private fun CurrentPlanSummary(
    billing: BillingOverviewDto,
    presentation: WorkspacePlanPresentation,
    action: @Composable () -> Unit,
) {
    val palette = ColorMode.current.toSitePalette()
    LCard(Modifier.fillMaxWidth().enterStaggered(0).attr("data-current-plan", "")) {
        Row(
            Modifier.fillMaxWidth().gap(Space.LG).styleModifier { property("flex-wrap", "wrap") },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(
                Modifier.gap(Space.XS).minWidth(0.px).styleModifier { property("flex", "1 1 240px") },
            ) {
                H3(Modifier.fontSize(TypeScale.HeadingSmall).fontWeight(700).margin(0.px).toAttrs()) {
                    Text(presentation.currentPlanName)
                }
                Span(Modifier.color(palette.textSecondary).fontSize(TypeScale.Small).toAttrs()) {
                    // One sentence with named holes. It was a `+` run whose separators, and the
                    // words "ends" and "renews", lived in Kotlin where no translator could reach
                    // them — and the two verbs mean opposite things to somebody reading a date.
                    val date = billing.renewsAt?.take(10)
                    val cadence = when {
                        date == null -> ""
                        billing.status == PlanStatus.CANCELING -> tf(
                            "app.projects.slug.settings.workspace_plan_usage.ends_on",
                            "ends {date}",
                            "date" to date,
                        )
                        else -> tf(
                            "app.projects.slug.settings.workspace_plan_usage.renews_on",
                            "renews {date}",
                            "date" to date,
                        )
                    }
                    Text(
                        tf(
                            "app.projects.slug.settings.workspace_plan_usage.status_seats_cadence",
                            "{status} · {seats}{cadence}",
                            "status" to billing.status.statusWords(),
                            "seats" to tPlural(
                                "app.projects.slug.settings.workspace_plan_usage.count.seat",
                                billing.seats.held,
                                "{count} owner/admin seat",
                                "{count} owner/admin seats",
                            ),
                            "cadence" to cadence.takeIf(String::isNotBlank)?.let { " · $it" }.orEmpty(),
                        ),
                    )
                }
            }
            Column(
                Modifier.gap(Space.XS).styleModifier { property("flex", "1 1 180px") },
                horizontalAlignment = Alignment.End,
            ) {
                Span(Modifier.tabularNumbers().fontSize(TypeScale.HeadingSmall).fontWeight(700).toAttrs()) {
                    Text(presentation.currentPrice)
                }
                Span(Modifier.color(palette.textMuted).fontSize(TypeScale.Micro).toAttrs()) {
                    Text(
                        presentation.currentPriceCadence?.let {
                            tf(
                                "app.projects.slug.settings.workspace_plan_usage.cadence_list_price_per_seat",
                                "{cadence} · list price per owner/admin seat",
                                "cadence" to it,
                            )
                        } ?: t("app.projects.slug.settings.workspace_plan_usage.no_subscription", "No subscription"),
                    )
                }
                action()
            }
        }
    }
}

@Composable
private fun CreditAllocation(billing: BillingOverviewDto, zeroNote: String?) {
    val palette = ColorMode.current.toSitePalette()
    val ceiling = billing.creditsIncluded + billing.creditsGranted
    val includedUsed = minOf(billing.creditsUsed, billing.creditsIncluded).coerceAtLeast(0)
    val grantedUsed = (billing.creditsUsed - billing.creditsIncluded)
        .coerceAtLeast(0)
        .coerceAtMost(billing.creditsGranted)
    val remaining = (ceiling - billing.creditsUsed).coerceAtLeast(0)
    LCard(Modifier.fillMaxWidth().enterStaggered(1).attr("data-credit-allocation", "")) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.Bottom) {
                Span(Modifier.tabularNumbers().fontSize(TypeScale.Heading).fontWeight(700).toAttrs()) {
                    Text(formatCount(billing.creditsRemaining))
                }
                Span(Modifier.color(palette.textSecondary).fontSize(TypeScale.BodySmall).toAttrs()) {
                    Text(t("app.projects.slug.settings.workspace_plan_usage.credits_left_this_month", "credits left this month"))
                }
            }
            LSegmentBar(
                segments = buildList {
                    // `LSegmentBar` re-serves each segment name to assistive technology, so an
                    // untranslated one here is untranslated in the only version of this bar a
                    // screen reader gets.
                    if (includedUsed > 0) {
                        add(
                            Triple(
                                t("app.projects.slug.settings.workspace_plan_usage.segment_included_used", "Included, used"),
                                includedUsed.toDouble(),
                                palette.accent,
                            ),
                        )
                    }
                    if (grantedUsed > 0) {
                        add(
                            Triple(
                                t("app.projects.slug.settings.workspace_plan_usage.segment_granted_used", "Granted, used"),
                                grantedUsed.toDouble(),
                                palette.borderStrong,
                            ),
                        )
                    }
                    if (remaining > 0) {
                        add(
                            Triple(
                                t("app.projects.slug.settings.workspace_plan_usage.segment_left", "Left"),
                                remaining.toDouble(),
                                palette.surfaceSunken,
                            ),
                        )
                    }
                },
                ariaLabel = t("app.projects.slug.settings.workspace_plan_usage.workspace_credits_this_month", "Workspace credits this month"),
            )
            LStatRow(
                rows = listOf(
                    // `LStatRow` re-serves each label to assistive technology, so an untranslated
                    // one here is untranslated in the only version of this bar a screen reader gets.
                    LStatRowItem(
                        t("app.projects.slug.settings.workspace_plan_usage.stat_included", "Included"),
                        formatCount(billing.creditsIncluded),
                    ),
                    LStatRowItem(
                        t("app.projects.slug.settings.workspace_plan_usage.stat_granted", "Granted"),
                        formatCount(billing.creditsGranted),
                    ),
                    LStatRowItem(
                        t("app.projects.slug.settings.workspace_plan_usage.stat_used", "Used"),
                        formatCount(billing.creditsUsed),
                    ),
                    LStatRowItem(
                        t("app.projects.slug.settings.workspace_plan_usage.stat_remaining", "Remaining"),
                        formatCount(billing.creditsRemaining),
                        emphasis = true,
                    ),
                ),
                caption = t("app.projects.slug.settings.workspace_plan_usage.credit_allocation_this_month", "Credit allocation this month"),
            )
            zeroNote?.let { LAlert(t("app.projects.slug.settings.workspace_plan_usage.no_lingvo_credits_left", "No Lingvo credits left"), it, variant = LAlertVariant.WARNING) }
        }
    }
}

@Composable
internal fun PlanComparison(
    billing: BillingOverviewDto,
    role: Role?,
    pending: Plan?,
    onChoose: (Plan) -> Unit,
) {
    val presentation = workspacePlanPresentation(billing, role)
    Div(cardGridModifier("plan").attr("data-plan-grid", "").toAttrs()) {
        presentation.cards.forEachIndexed { index, card ->
            LCard(
                Modifier.fillMaxWidth().enterStaggered(index + 4).attr("data-plan-card", card.plan.name),
            ) {
                Column(Modifier.fillMaxWidth().gap(Space.SM).styleModifier { property("height", "100%") }) {
                    Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                        H3(Modifier.fontSize(TypeScale.Body).fontWeight(700).margin(0.px).toAttrs()) {
                            Text(card.title)
                        }
                        Spacer()
                        if (card.current) LBadge(t("app.projects.slug.settings.workspace_plan_usage.current", "Current"), variant = LBadgeVariant.SUCCESS)
                    }
                    Span(Modifier.tabularNumbers().fontSize(TypeScale.Heading).fontWeight(700).toAttrs()) {
                        Text(if (card.plan == Plan.FREE) t("app.projects.slug.settings.workspace_plan_usage.free", "Free") else card.price.display)
                    }
                    Span(Modifier.color(ColorMode.current.toSitePalette().textSecondary).fontSize(TypeScale.Small).toAttrs()) {
                        Text(
                            if (card.plan == Plan.FREE) {
                                t(
                                    "app.projects.slug.settings.workspace_plan_usage.no_payment_card_required",
                                    "No payment card required",
                                )
                            } else {
                                tf(
                                    "app.projects.slug.settings.workspace_plan_usage.interval_per_seat",
                                    "{interval} per owner/admin seat",
                                    "interval" to card.interval.displayName,
                                )
                            },
                        )
                    }
                    Div(
                        Modifier
                            .fillMaxWidth()
                            .backgroundColor(ColorMode.current.toSitePalette().accentSubtle)
                            .borderRadius(Radius.Control)
                            .padding(Space.SM)
                            .toAttrs(),
                    ) {
                        Span(Modifier.fontSize(TypeScale.Small).fontWeight(600).toAttrs()) {
                            Text(tPlural(
                                "app.projects.slug.settings.workspace_plan_usage.credits_per_month",
                                card.plan.creditsFor(billing.seats.held),
                                "{count} workspace credit per month",
                                "{count} workspace credits per month",
                            ))
                        }
                    }
                    Spacer()
                    Div(Modifier.fillMaxWidth().attr("data-plan-footer", "").toAttrs()) {
                        if (card.current) {
                            Span(
                                Modifier
                                    .fillMaxWidth()
                                    .color(ColorMode.current.toSitePalette().textSecondary)
                                    .fontSize(TypeScale.Small)
                                    .fontWeight(600)
                                    .styleModifier { property("text-align", "center") }
                                    .toAttrs(),
                            ) { Text(card.actionLabel) }
                        } else {
                            LButton(
                                card.actionLabel,
                                { if (card.actionEnabled) onChoose(card.plan) },
                                Modifier.fillMaxWidth(),
                                variant = if (card.actionEnabled) LButtonVariant.PRIMARY else LButtonVariant.SECONDARY,
                                enabled = card.actionEnabled && pending == null,
                                loading = pending == card.plan,
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun BillingStatusAlert(billing: BillingOverviewDto) {
    when (billing.status) {
        PlanStatus.PAST_DUE -> LAlert(t("app.projects.slug.settings.workspace_plan_usage.payment_needs_attention", "Payment needs attention"), t("app.projects.slug.settings.workspace_plan_usage.the_workspace_keeps_its_plan_while", "The workspace keeps its plan while payment is retried."), variant = LAlertVariant.WARNING)
        PlanStatus.CANCELING -> LAlert(t("app.projects.slug.settings.workspace_plan_usage.subscription_ending", "Subscription ending"), tf(
            "app.projects.slug.settings.workspace_plan_usage.the_workspace_moves_to_free_after",
            "The workspace moves to Free after {date}; nothing is deleted.",
            "date" to (
                billing.renewsAt?.take(10)
                    ?: t("app.projects.slug.settings.workspace_plan_usage.this_period", "this period")
            ),
        ), variant = LAlertVariant.INFO)
        else -> Unit
    }
}

internal fun cardGridModifier(kind: String): Modifier = Modifier
    .fillMaxWidth()
    .gap(Space.MD)
    .styleModifier {
        property("display", "grid")
        property("grid-template-columns", "repeat(auto-fit, minmax(min(100%, 320px), 1fr))")
        property("align-items", "stretch")
    }
    .attr("data-card-grid-kind", kind)
